import React from 'react';
import Button from 'components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faFileArchive } from '@fortawesome/pro-solid-svg-icons';
import { downloadJpg } from 'api/utils';
import store from 'store';
import DevModalButton from 'components/modal/DevModalButton';
import { addTextArrToTextObjects, addImagemagickToImages } from 'actions/scene';
import './Dev.scss';
import { storeGetState } from 'utils/utils';
import SceneHelper from 'utils/SceneHelper';

const filterAssetsByImageIds = (assets = [], imageIds = []) => {
  const imageIdsSet = new Set(imageIds);
  return assets
    .filter((asset) => imageIdsSet.has(asset.id))
    .map(({ id, src, originalSrc }) => ({ id, src, originalSrc }));
};

const getCurrentDesignId = (storeState) => {
  const idFromStore = storeState?.app?.canvasId;
  if (idFromStore !== undefined && idFromStore !== null && idFromStore !== '') {
    return idFromStore;
  }
  const idFromUrl = new URLSearchParams(window.location.search).get('id');
  if (idFromUrl !== undefined && idFromUrl !== null && idFromUrl !== '') {
    return idFromUrl;
  }

  const idFromScene = storeState?.scene?.config?.canvasId;
  if (idFromScene !== undefined && idFromScene !== null && idFromScene !== '') {
    return idFromScene;
  }

  return null;
};

const getExportFileNameBase = (storeState) => {
  const currentId = getCurrentDesignId(storeState);
  if (currentId) {
    return String(currentId);
  }

  return `design-${new Date().toISOString().replace(/[:.]/g, '-')}`;
};

const downloadJson = (data, fileName) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const URLApi = window.URL || window.webkitURL;
  const url = URLApi.createObjectURL(blob);

  const a = document.createElement('a');
  a.download = fileName;
  a.type = 'application/json';
  a.href = url;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URLApi.revokeObjectURL(url), 0);
};

const getMockDesignData = (storeState, id) => {
  const { scene, photos: { photos = [], backgrounds = [], clipart = [] } = {} } = storeState;
  const imageIds = SceneHelper.getAllImageIds(storeState);

  const normalizedId = Number.isNaN(Number(id)) ? id : Number(id);
  return {
    id: normalizedId,
    type: 2,
    category_id: scene?.config?.categoryId || null,
    product_id: scene?.config?.productId || null,
    productoption_id: scene?.config?.productOptionId || null,
    size_id: 0,
    theme_id: null,
    name: 'Design',
    scene: {
      ...scene,
      assets: {
        photos: filterAssetsByImageIds(photos, imageIds),
        backgrounds: filterAssetsByImageIds(backgrounds, imageIds),
        clipart: filterAssetsByImageIds(clipart, imageIds),
      },
    },
  };
};

const DownloadExportButton = ({ onlyCurrentPage }) => {
  const handleSave = React.useCallback(async () => {
    try {
      store.dispatch(addTextArrToTextObjects());
      const { scene } = storeGetState();
      await downloadJpg(scene, { renderMask: true, onlyCurrentPage });
    } catch (error) {
      console.warn('[DEV] Failed to export file from developer tools.', error);
    }
  }, [onlyCurrentPage]);

  const tooltipText = onlyCurrentPage ? '[DEV] Download current page JPG' : '[DEV] Download all pages JPG';

  return (
    <Button onClick={handleSave} className="download-button" data-for="bottomTooltip" data-tip={tooltipText}>
      <FontAwesomeIcon.Memo icon={onlyCurrentPage ? faFileDownload : faFileArchive} />
    </Button>
  );
};

const DownloadDesignDataButton = () => {
  const handleSave = React.useCallback(() => {
    store.dispatch(addTextArrToTextObjects());
    store.dispatch(addImagemagickToImages());
    const storeState = storeGetState();
    const id = getCurrentDesignId(storeState);
    const fileNameBase = getExportFileNameBase(storeState);
    const payload = getMockDesignData(storeState, id);
    downloadJson(payload, `${fileNameBase}.json`);
  }, []);

  return (
    <Button
      onClick={handleSave}
      className="download-button"
      data-for="bottomTooltip"
      data-tip="[DEV] Download full design mock JSON"
    >
      JSON
    </Button>
  );
};

const Dev = ({ desktop, showTitleOnDesktop, title }) => (
  <div className="dev-tab">
    {desktop && showTitleOnDesktop ? <h2>{title}</h2> : null}
    <DownloadExportButton onlyCurrentPage />
    <DevModalButton />
    <DownloadDesignDataButton />
    <DownloadExportButton />
  </div>
);

export default Dev;
